import { useEffect, useState, useCallback, useRef, useMemo } from 'react'
import { useLocation } from '@tanstack/react-router'
import { MessageList } from './MessageList'
import { MessageInput } from './MessageInput'
import { AgentNotificationBanner } from './AgentNotificationBanner'
import { LowCreditsBanner } from '../billing/LowCreditsBanner'
import { AgentChoicePrompt } from './AgentChoicePrompt'
import { GuidedWorkflowPanel } from '@/components/workflow/GuidedWorkflowPanel'
import { useConversationStore } from '@/stores/conversationStore'
import { useMessageStore } from '@/stores/messageStore'
import { useVideoWorkspaceStore } from '@/stores/videoWorkspaceStore'
import { useWorkflowEvents } from '@/hooks/useWorkflowEvents'
import { useSlideThinkingSync } from '@/hooks/useSlideThinkingSync'
import { useMessageStatusMonitor } from '@/hooks/useMessageStatusMonitor'
import { useAgentEscalation } from '@/hooks/useAgentEscalation'
import { conversationAPI } from '@/services/api'
import { unifiedMessageService } from '@/services/unifiedMessageService'
import { cn } from '@/lib/utils'

// Constants for pagination
const INITIAL_MESSAGE_COUNT = 20  // Start with last 20 messages
const LOAD_MORE_COUNT = 30        // Load 30 more each time

export function ChatArea() {
  const location = useLocation()
  const { activeConversationId } = useConversationStore()

  // Hide the workflow panel when in video workspace (it has its own layout)
  const isVideoWorkspace = location.pathname.includes('/video-workspace')

  // Get active chat context for message filtering (outline vs narration)
  const activeChatContext = useVideoWorkspaceStore(state => state.activeChatContext)

  const {
    getMessagesForConversation,
    setMessages,
    setLoading,
    setError,
    isLoading,
    clearConversationState,
    findIncompleteStreamingMessage,
    restoreMessageFromChunks,
    setStreamingMessage,
    streamingMessageId
  } = useMessageStore()

  // Enable workflow event detection
  useWorkflowEvents(activeConversationId || '')

  // Monitor message status for workflow planning
  useMessageStatusMonitor(activeConversationId || '')

  // Agent escalation state (notifications and choice prompts)
  const {
    notification,
    choiceRequest,
    dismissNotification,
    submitChoice
  } = useAgentEscalation()

  // Sync slide thinking from videoWorkspaceStore to message's currentThinkingSubject
  // This bridges the parent SSE events to the child conversation's message display
  useSlideThinkingSync(activeConversationId)

  // Listen for message completion events from child slide conversations
  // This handles the case where the per-message SSE isn't connected (viewing from parent workspace)
  useEffect(() => {
    if (!activeConversationId) return

    const handleMessageComplete = async (event: Event) => {
      const customEvent = event as CustomEvent
      const { conversationId: completedConvId, messageId, context } = customEvent.detail

      console.log('📬 ChatArea: Received message-completed event:', {
        completedConvId: completedConvId?.slice(0, 8),
        activeConversationId: activeConversationId?.slice(0, 8),
        match: completedConvId === activeConversationId,
        messageId: messageId?.slice(0, 8),
        context,
      })

      if (completedConvId === activeConversationId) {
        console.log('📬 ChatArea: Message completed, refreshing messages:', {
          conversationId: completedConvId,
          messageId,
        })

        try {
          // Clear streaming state immediately for instant UI feedback
          const { setStreamingMessage: clearStreaming } = useMessageStore.getState()
          clearStreaming(null)
          console.log('📬 ChatArea: Cleared streaming message state')

          // Also update slideConversation status if this is a child slide conversation
          // This ensures filmstrip shows completed state immediately
          const { slideConversations, updateSlideConversation, clearNarrationJobState } = useVideoWorkspaceStore.getState()
          const slideConv = slideConversations.find(sc => sc.id === completedConvId)
          if (slideConv) {
            console.log('📬 ChatArea: Updating slide status to completed', {
              outlineSlideId: slideConv.outlineSlideId,
            })
            updateSlideConversation(slideConv.outlineSlideId, {
              status: 'completed',
              generatingThinking: undefined,
            })
          }

          // Clear narration job state if this was a narration message
          // Note: The useNarrationJobStatus hook also listens for scripts-batch-updated,
          // but this provides an additional safety net for message-completed events
          if (context?.source === 'narration') {
            console.log('🎤 ChatArea: Clearing narration job state after completion')
            clearNarrationJobState(completedConvId)
          }

          // Refresh messages from API to get the final content
          const response = await conversationAPI.getMessages(activeConversationId)
          setMessages(activeConversationId, response.messages)
          console.log('📬 ChatArea: Messages refreshed successfully', {
            messageCount: response.messages?.length,
          })
        } catch (error) {
          console.error('📬 ChatArea: Failed to refresh messages after completion:', error)
        }
      }
    }

    window.addEventListener('message-completed', handleMessageComplete)
    return () => window.removeEventListener('message-completed', handleMessageComplete)
  }, [activeConversationId, setMessages])

  const allMessages = activeConversationId ? getMessagesForConversation(activeConversationId) : []

  // Filter messages based on context (for parent VIDEO conversations only)
  // Outline and narration contexts share the same parent conversation but have separate message histories
  const messages = useMemo(() => {
    if (!activeConversationId) return []

    // Only filter for outline/narration contexts (parent VIDEO conversation)
    if (activeChatContext === 'outline') {
      // Show messages without source (legacy) OR with source='outline'
      return allMessages.filter(msg => {
        const source = (msg.metadata as any)?.context?.source
        return !source || source === 'outline'
      })
    } else if (activeChatContext === 'narration') {
      // Show only messages with source='narration'
      return allMessages.filter(msg => {
        const source = (msg.metadata as any)?.context?.source
        return source === 'narration'
      })
    }

    // For slide conversations or broadcast, show all messages
    return allMessages
  }, [activeConversationId, activeChatContext, allMessages])

  const isCompact = false // Can be made dynamic based on viewport

  // Pagination state
  const [hasMoreMessages, setHasMoreMessages] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [totalMessages, setTotalMessages] = useState(0)
  const messageListRef = useRef<HTMLDivElement>(null)

  // Track if initial load is complete
  const [, setInitialLoadComplete] = useState(false)

  // Load messages when conversation changes
  useEffect(() => {
    let isStale = false

    const loadMessages = async () => {
      if (!activeConversationId) return

      console.log('💬 ChatArea: Loading messages for conversation:', activeConversationId)

      // Reset initial load state for new conversation
      setInitialLoadComplete(false)

      // Clear global state from other conversations
      clearConversationState(activeConversationId)

      // Close any active streams from other conversations
      const activeStreams = unifiedMessageService.getActiveStreams()
      activeStreams.forEach(streamConversationId => {
        if (streamConversationId !== activeConversationId) {
          console.log('🔌 ChatArea: Closing stream for previous conversation:', streamConversationId)
          unifiedMessageService.closeStream(streamConversationId)
        }
      })

      try {
        setLoading(true)
        setError(null)

        // Load only recent messages initially for instant loading
        const response = await conversationAPI.getMessages(activeConversationId, {
          limit: INITIAL_MESSAGE_COUNT
        })

        // Abort if this effect was superseded (React Strict Mode double-mount
        // or conversation changed before API returned)
        if (isStale) {
          console.log('💬 ChatArea: Aborting stale loadMessages for:', activeConversationId)
          return
        }

        // RE-CAPTURE any streaming/placeholder messages from the current store
        // These may have been created by autoSend during the async API gap
        const currentMessages = getMessagesForConversation(activeConversationId)
        const liveStreamingMessages = currentMessages.filter(msg =>
          (msg.metadata?.isPlaceholder === true) ||
          (msg.status === 'streaming' && msg.metadata?.isStreaming)
        )

        // Build a map of streaming state by message ID for merging
        // This preserves streaming/thinking state when API returns the same message
        // without client-side metadata (status: 'streaming', thinkingHistory, etc.)
        const streamingStateById = new Map<string, typeof currentMessages[0]>()
        for (const msg of currentMessages) {
          if (msg.status === 'streaming' || msg.metadata?.isStreaming || msg.metadata?.isPlaceholder) {
            streamingStateById.set(msg.id, msg)
          }
        }

        if (liveStreamingMessages.length > 0) {
          console.log('🎯 ChatArea: Found live streaming messages during loadMessages:', liveStreamingMessages.map(m => m.id))
        }

        // Check if API returned real AI messages (not placeholders)
        const hasRealAIMessages = response.messages.some(msg =>
          msg.role === 'assistant' && !msg.metadata?.isPlaceholder
        )

        let finalMessages = response.messages

        // Case 1: No real AI messages from API, but we have local streaming/placeholder messages
        // → Add them to the message list (first-message scenario, placeholder not yet in DB)
        if (liveStreamingMessages.length > 0 && !hasRealAIMessages) {
          console.log('🎯 ChatArea: Re-adding placeholder messages for instant feedback')
          const storedPlaceholderId = (window as any).__placeholderMessageId

          const userMessage = response.messages.find(msg => msg.role === 'user')
          if (userMessage) {
            const nonUserMessages = response.messages.filter(msg => msg.role !== 'user')
            finalMessages = [userMessage, ...liveStreamingMessages, ...nonUserMessages]
          } else {
            finalMessages = [...response.messages, ...liveStreamingMessages]
          }

          const streamingPlaceholder = liveStreamingMessages.find(msg =>
            msg.status === 'streaming'
          )
          if (streamingPlaceholder) {
            console.log('🎯 ChatArea: Restoring streaming state for placeholder:', streamingPlaceholder.id)
            if (!storedPlaceholderId || storedPlaceholderId !== streamingPlaceholder.id) {
              (window as any).__placeholderMessageId = streamingPlaceholder.id
            }
          }
        }

        // Case 2: API returned real AI messages that also exist in store with streaming state
        // → Merge the streaming state into the API messages so setMessages doesn't wipe it
        if (streamingStateById.size > 0) {
          finalMessages = finalMessages.map(msg => {
            const streamingMsg = streamingStateById.get(msg.id)
            if (streamingMsg) {
              console.log('🎯 ChatArea: Merging streaming state into API message:', msg.id)
              return {
                ...msg,
                status: streamingMsg.status,
                metadata: { ...msg.metadata, ...streamingMsg.metadata },
                thinkingHistory: streamingMsg.thinkingHistory?.length ? streamingMsg.thinkingHistory : msg.thinkingHistory,
                currentThinkingSubject: streamingMsg.currentThinkingSubject || msg.currentThinkingSubject,
                currentThinking: streamingMsg.currentThinking || msg.currentThinking,
              }
            }
            return msg
          })
        }

        // Deduplicate messages by ID before setting (prevents React key warnings)
        const uniqueMessages = Array.from(
          new Map(finalMessages.map(msg => [msg.id, msg])).values()
        )

        if (uniqueMessages.length !== finalMessages.length) {
          console.warn(`⚠️ Removed ${finalMessages.length - uniqueMessages.length} duplicate messages during initial load`)
        }

        if (isStale) return
        setMessages(activeConversationId, uniqueMessages)

        // Restore streaming message ID if we have streaming messages
        const currentStreamingId = streamingMessageId
        const activeStreamingMsg = liveStreamingMessages.find(msg => msg.status === 'streaming') ||
          [...streamingStateById.values()].find(msg => msg.status === 'streaming')
        if (activeStreamingMsg) {
          const streamingId = currentStreamingId || activeStreamingMsg.id
          setStreamingMessage(streamingId)
          console.log('✅ ChatArea: Streaming state restored:', streamingId)
        }

        // Check if there are more messages to load
        // The API returns pagination info
        const totalCount = response.pagination?.total || response.messages.length
        setTotalMessages(totalCount)
        // Fix: Use || instead of ?? to handle undefined/false properly
        const hasMore = response.pagination?.hasMore || totalCount > INITIAL_MESSAGE_COUNT
        setHasMoreMessages(hasMore)

        console.log('📊 Initial load pagination:', {
          totalCount,
          loadedCount: response.messages.length,
          hasMore,
          pagination: response.pagination
        })

        if (hasMore) {
          console.log(`📚 ChatArea: Loaded ${INITIAL_MESSAGE_COUNT} recent messages. ${totalCount - INITIAL_MESSAGE_COUNT} older messages available.`)
        }

        // Smart Stream Restoration
        if (!isStale) await performStreamRestoration(activeConversationId)

        // Mark initial load as complete and signal readiness
        if (isStale) return
        setInitialLoadComplete(true)
        console.log('💬 ChatArea: Initial load complete, signaling readiness for conversation:', activeConversationId)

        // Dispatch event to signal that ChatArea is ready with messages loaded
        window.dispatchEvent(new CustomEvent('chat-area-ready', {
          detail: { conversationId: activeConversationId }
        }))

        // Scroll to bottom after initial load to show latest messages
        setTimeout(() => {
          if (messageListRef.current) {
            messageListRef.current.scrollTop = messageListRef.current.scrollHeight
            console.log('📍 ChatArea: Scrolled to bottom to show latest messages')
          }
        }, 100) // Small delay to ensure DOM is updated

      } catch (error) {
        if (isStale) return
        console.error('❌ ChatArea: Failed to load messages:', error)
        setError(error instanceof Error ? error.message : 'Failed to load messages')
      } finally {
        if (!isStale) setLoading(false)
      }
    }

    const performStreamRestoration = async (conversationId: string) => {
      try {
        console.log('🔄 ChatArea: Starting stream restoration for:', conversationId)

        // Check for incomplete streaming messages
        const incompleteMessage = findIncompleteStreamingMessage(conversationId)

        if (incompleteMessage) {
          console.log('📨 Found incomplete streaming message:', incompleteMessage.id)

          try {
            const chunksResponse = await conversationAPI.getAllChunks(conversationId, incompleteMessage.id)

            if (chunksResponse.success && chunksResponse.data.chunks.length > 0) {
              console.log(`📦 Restoring ${chunksResponse.data.chunks.length} chunks for message ${incompleteMessage.id}`)

              restoreMessageFromChunks(
                incompleteMessage.id,
                chunksResponse.data.chunks,
                chunksResponse.data.reconstructedContent
              )

              console.log('✅ Message restored from chunks')
            }
          } catch (chunkError) {
            console.warn('⚠️ Failed to restore chunks for message:', incompleteMessage.id, chunkError)
          }
        }

        // Check for active message jobs (PENDING or PROCESSING)
        // This provides immediate feedback when user refreshes during job execution
        // Set streamingMessageId so UnifiedThinkingIndicator shows in the message bubble
        try {
          const activeJobsResponse = await conversationAPI.getActiveMessageJobs(conversationId)
          if (activeJobsResponse.hasActiveJob && activeJobsResponse.activeMessageId) {
            console.log('🔔 ChatArea: Found active message job:', {
              messageId: activeJobsResponse.activeMessageId,
              jobCount: activeJobsResponse.jobs.length
            })
            // Set streaming message so UI shows processing state
            setStreamingMessage(activeJobsResponse.activeMessageId)

            // Also update slideConversation status if this is a child slide conversation
            // This ensures filmstrip shows correct generating state after refresh
            const { useVideoWorkspaceStore } = await import('@/stores/videoWorkspaceStore')
            const { slideConversations, updateSlideConversation } = useVideoWorkspaceStore.getState()
            const slideConv = slideConversations.find(sc => sc.id === conversationId)
            if (slideConv && slideConv.status !== 'generating') {
              console.log('🎬 ChatArea: Updating slide status to generating on load', {
                outlineSlideId: slideConv.outlineSlideId,
                conversationId
              })
              updateSlideConversation(slideConv.outlineSlideId, {
                status: 'generating',
                // Keep existing thinking or show default - outline fetch will update with persisted thinking
                generatingThinking: slideConv.generatingThinking || 'Processing...',
              })
            }
          }
        } catch (jobError) {
          console.warn('⚠️ Failed to check for active jobs:', jobError)
        }

        // Check for active narration jobs specifically (for VIDEO conversations with narration context)
        // This ensures narration script generation persists across navigation/refresh
        try {
          const { useVideoWorkspaceStore } = await import('@/stores/videoWorkspaceStore')
          const { activeChatContext, setNarrationJobState } = useVideoWorkspaceStore.getState()

          if (activeChatContext === 'narration') {
            const narrationJobsResponse = await conversationAPI.getActiveMessageJobs(
              conversationId,
              { contextSource: 'narration' }
            )

            if (narrationJobsResponse.hasActiveJob && narrationJobsResponse.activeMessageId) {
              console.log('🎤 ChatArea: Found active narration job:', {
                messageId: narrationJobsResponse.activeMessageId,
                conversationId
              })

              // Update narration job state in store (persists across navigation)
              setNarrationJobState(conversationId, {
                isGenerating: true,
                activeMessageId: narrationJobsResponse.activeMessageId,
                activeJobId: narrationJobsResponse.jobs[0]?.jobId || null,
              })

              // Reconnect SSE for narration job
              const { realtimeStreamManager } = await import('@/services/realtimeStreamManager')
              setStreamingMessage(narrationJobsResponse.activeMessageId)
              realtimeStreamManager.startStream(
                narrationJobsResponse.activeMessageId,
                conversationId,
                0
              )
            }
          }
        } catch (narrationJobError) {
          console.warn('⚠️ Failed to check for active narration jobs:', narrationJobError)
        }

        // Check if conversation is still actively streaming
        const streamStatus = await conversationAPI.getStreamStatus(conversationId)

        if (streamStatus.success && streamStatus.data.isStreaming && streamStatus.data.messageId) {
          console.log('🔗 Reconnecting to active stream:', {
            conversationId,
            messageId: streamStatus.data.messageId,
            lastChunkIndex: streamStatus.data.lastChunkIndex
          })
          // Import realtimeStreamManager directly to start stream with correct messageId
          const { realtimeStreamManager } = await import('@/services/realtimeStreamManager')
          // Set streaming message in store so UI updates correctly
          setStreamingMessage(streamStatus.data.messageId)
          // Start the SSE stream from where it left off
          realtimeStreamManager.startStream(
            streamStatus.data.messageId,
            conversationId,
            streamStatus.data.lastChunkIndex || 0
          )
        }

      } catch (error) {
        console.error('❌ Stream restoration failed:', error)
      }
    }

    loadMessages()
    return () => { isStale = true }
  }, [activeConversationId, setMessages, setLoading, setError, clearConversationState, findIncompleteStreamingMessage, restoreMessageFromChunks])

  // Load more messages when scrolling up
  const loadMoreMessages = useCallback(async () => {
    if (!activeConversationId || loadingMore || !hasMoreMessages) return

    try {
      setLoadingMore(true)

      // We need to load messages BEFORE the ones we currently have
      // The backend returns messages sorted by timestamp ascending (oldest first)
      // We already have the most recent messages, so we need to get older ones
      const currentMessageCount = messages.length

      // Match the original working implementation
      const offset = Math.max(0, totalMessages - currentMessageCount - LOAD_MORE_COUNT)
      const limit = Math.min(LOAD_MORE_COUNT, totalMessages - currentMessageCount)

      console.log(`📚 Loading ${limit} more messages (offset: ${offset}, total: ${totalMessages}, current: ${currentMessageCount})...`)

      const response = await conversationAPI.getMessages(activeConversationId, {
        limit,
        offset
      })

      if (response.messages.length > 0) {
        // Save current scroll height before adding messages
        const container = messageListRef.current
        const previousScrollHeight = container?.scrollHeight || 0

        // Prepend older messages to the beginning
        const existingMessages = getMessagesForConversation(activeConversationId)
        const combinedMessages = [...response.messages, ...existingMessages]

        // Deduplicate messages by ID (prevents React key warnings)
        const uniqueMessages = Array.from(
          new Map(combinedMessages.map(msg => [msg.id, msg])).values()
        )

        if (uniqueMessages.length !== combinedMessages.length) {
          console.warn(`⚠️ Removed ${combinedMessages.length - uniqueMessages.length} duplicate messages during load more`)
        }

        setMessages(activeConversationId, uniqueMessages)

        // Restore scroll position after messages are added
        setTimeout(() => {
          if (container) {
            const newScrollHeight = container.scrollHeight
            const scrollDiff = newScrollHeight - previousScrollHeight
            container.scrollTop = container.scrollTop + scrollDiff
            console.log(`📜 Maintained scroll position after loading ${response.messages.length} older messages`)
          }
        }, 0)

        // Update hasMoreMessages
        const newTotal = uniqueMessages.length
        setHasMoreMessages(newTotal < totalMessages)

        console.log(`📊 Message status: ${newTotal}/${totalMessages} loaded, hasMore: ${newTotal < totalMessages}`)
      }
    } catch (error) {
      console.error('Failed to load more messages:', error)
    } finally {
      setLoadingMore(false)
    }
  }, [activeConversationId, messages.length, totalMessages, hasMoreMessages, loadingMore, getMessagesForConversation, setMessages])

  // Auto-load more messages when scrolling near top
  useEffect(() => {
    const handleScroll = () => {
      if (!messageListRef.current || loadingMore || !hasMoreMessages) return

      const { scrollTop } = messageListRef.current

      // Trigger load when within 100px of top
      if (scrollTop < 100 && !loadingMore && hasMoreMessages) {
        console.log('🔄 Triggering loadMoreMessages')
        loadMoreMessages()
      }
    }

    const scrollContainer = messageListRef.current
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll)
      return () => scrollContainer.removeEventListener('scroll', handleScroll)
    }
  }, [loadMoreMessages, loadingMore, hasMoreMessages])

  // Scroll to bottom when new messages arrive (for streaming/new messages)
  useEffect(() => {
    if (!messageListRef.current || messages.length === 0) return

    // Check if user is near bottom (within 200px)
    const container = messageListRef.current
    const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 200

    // Only auto-scroll if user is already near the bottom
    // This prevents annoying jumps when user is reading older messages
    if (isNearBottom) {
      container.scrollTop = container.scrollHeight
    }
  }, [messages.length]) // Trigger when message count changes

  if (!activeConversationId) {
    return (
      <div className="flex flex-col h-full bg-transparent">
        {/* Empty State */}
        <div className="flex-1 flex items-center justify-center overflow-y-auto p-8">
          <div className="text-center max-w-md">
            <div className="w-20 h-20 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6 animate-in-fade">
              <svg className="w-10 h-10 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <h2 className="text-2xl font-semibold mb-2 text-primary">Welcome to Ivy Chat</h2>
            <p className="text-muted-foreground mb-6">
              Your intelligent AI assistant is ready to help
            </p>
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 shadow-lg">
              <p className="text-sm text-muted-foreground">
                <a href="/" className="text-primary hover:underline font-medium">Go to the Hero page</a> to start a new conversation
              </p>
            </div>
          </div>
        </div>

        {/* Input Area - floating container */}
        <div className={cn(
          "flex-shrink-0 relative -mt-3 mx-4 mb-4",
          isCompact ? "mx-2 mb-2" : ""
        )}>
          <div className="floating-input rounded-2xl overflow-hidden">
            <MessageInput />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full bg-transparent">
      {/* Guided Workflow Panel - Above scrollable area (hidden in video workspace) */}
      {activeConversationId && !isVideoWorkspace && <GuidedWorkflowPanel conversationId={activeConversationId} />}

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto relative scrollbar-thin px-2" ref={messageListRef}>
        {/* Load More Button/Indicator */}
        {hasMoreMessages && (
          <div className="sticky top-0 z-10 flex justify-center py-4 bg-gradient-to-b from-background/80 to-transparent backdrop-blur-sm">
            {loadingMore ? (
              <div className="flex items-center gap-2 px-4 py-2 bg-background/80 backdrop-blur rounded-full border border-white/10 shadow-sm">
                <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                <span className="text-xs font-medium text-muted-foreground">Loading older messages...</span>
              </div>
            ) : (
              <button
                onClick={loadMoreMessages}
                className="px-4 py-2 bg-background/80 backdrop-blur rounded-full border border-white/10 hover:bg-accent/50 transition-all duration-200 shadow-sm hover:shadow-md active:scale-95"
              >
                <span className="text-xs font-medium text-muted-foreground">
                  Load older messages ({totalMessages - messages.length} remaining)
                </span>
              </button>
            )}
          </div>
        )}

        {isLoading && messages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center animate-pulse">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
              </div>
              <h3 className="text-lg font-medium mb-2">Loading conversation</h3>
              <p className="text-sm text-muted-foreground">Fetching your messages...</p>
            </div>
          </div>
        ) : (
          <div className="pb-4">
            <MessageList
              messages={messages}
              conversationId={activeConversationId}
            />
          </div>
        )}
      </div>

      <div className={cn("flex-shrink-0 mx-4 mb-2 empty:hidden", isCompact ? "mx-2" : "")}>
        <LowCreditsBanner refreshKey={activeConversationId} />
      </div>

      {/* Agent Escalation UI - Above input area */}
      {(notification || choiceRequest) && (
        <div className={cn(
          "flex-shrink-0 mx-4 mb-2 space-y-2 z-20",
          isCompact ? "mx-2" : ""
        )}>
          {/* Agent Notification Banner */}
          {notification && (
            <AgentNotificationBanner
              message={notification.message}
              severity={notification.severity}
              context={notification.context}
              onDismiss={dismissNotification}
            />
          )}

          {/* Agent Choice Prompt (blocking) */}
          {choiceRequest && (
            <AgentChoicePrompt
              requestId={choiceRequest.requestId}
              question={choiceRequest.question}
              context={choiceRequest.context}
              options={choiceRequest.options}
              recommendation={choiceRequest.recommendation}
              onSelect={submitChoice}
            />
          )}
        </div>
      )}

      {/* Input Area - floating container */}
      <div className={cn(
        "flex-shrink-0 relative mx-4 mb-4 z-20",
        isCompact ? "mx-2 mb-2" : ""
      )}>
        <div className="floating-input rounded-2xl overflow-hidden transition-all duration-200 focus-within:ring-2 focus-within:ring-primary/20">
          <MessageInput />
        </div>
      </div>
    </div>
  )
}